import { useMutation, useQuery } from '@tanstack/react-query'
import { publicApi, getStoredAuth } from './client'
import type {
  AvailabilityResult,
  BookingStatus,
  Customer,
  CustomerReservation,
  HotelProfile,
  ReservationSummary,
  SearchState,
} from './types'

// Django-served media (local/dev: http://web:8000/media/…) is made relative so
// the /media proxy serves it. Every other absolute URL — Cloud Storage uploads
// in production, Wikimedia defaults in page_content — must stay untouched: the
// public nginx has no route for those paths and would answer with index.html.
function stripDomain(url: string | undefined | null): string {
  if (!url) return ''
  try {
    const u = new URL(url)
    return u.pathname.startsWith('/media/') ? u.pathname : url
  } catch {
    return url
  }
}

function fixImageUrls<T>(obj: T): T {
  if (!obj || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(fixImageUrls) as T
  const result = { ...obj } as Record<string, unknown>
  for (const key of Object.keys(result)) {
    if (typeof result[key] === 'string' && (key.endsWith('_image') || key === 'image') && (result[key] as string).startsWith('http')) {
      result[key] = stripDomain(result[key] as string)
    } else if (typeof result[key] === 'object' && result[key] !== null) {
      result[key] = fixImageUrls(result[key])
    }
  }
  return result as T
}

export function useHotelProfile() {
  return useQuery({
    queryKey: ['public-hotel-profile'],
    queryFn: async () => {
      const response = await publicApi.get<HotelProfile>('/public/hotel-profile/')
      return fixImageUrls(response.data)
    },
  })
}

export function useBookingStatus() {
  return useQuery({
    queryKey: ['public-booking-status'],
    queryFn: async () => {
      const response = await publicApi.get<BookingStatus>('/public/booking-status/')
      return response.data
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
    retry: 1,
  })
}

export function useAvailability() {
  return useMutation({
    mutationFn: async (params: SearchState) => {
      const { adultsManuallySet, ...query } = params
      const response = await publicApi.get<AvailabilityResult[]>('/public/availability/', {
        params: { ...query, adults: adultsManuallySet === false ? undefined : query.adults },
      })
      return fixImageUrls(response.data)
    },
  })
}

export function useCreateReservation() {
  return useMutation({
    mutationFn: async (payload: {
      room_type_id: number
      check_in_date: string
      check_out_date: string
      adults: number
      children: number
      account_mode: string
      special_requests: string
      guest: { first_name: string; last_name: string; email: string; phone: string }
    }) => {
      const auth = getStoredAuth()
      const response = await publicApi.post<ReservationSummary>(
        '/public/reservations/',
        payload,
        auth ? { headers: { Authorization: `Bearer ${auth.token}` } } : undefined,
      )
      return response.data
    },
  })
}

export function useCustomerLogin() {
  return useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      const response = await publicApi.post<{ token: string; customer: Customer }>('/public/auth/login/', data)
      return response.data
    },
  })
}

export function useCustomerRegister() {
  return useMutation({
    mutationFn: async (data: {
      first_name: string
      last_name: string
      email: string
      phone: string
      password: string
    }) => {
      const response = await publicApi.post<{ token: string; customer: Customer }>('/public/auth/register/', data)
      return response.data
    },
  })
}

export function useCustomerReservations(customerId: number | undefined) {
  return useQuery({
    queryKey: ['customer-reservations', customerId],
    queryFn: async () => {
      const token = getStoredAuth()?.token
      const response = await publicApi.get<CustomerReservation[]>('/public/me/reservations/', {
        headers: { Authorization: `Bearer ${token}` },
      })
      return response.data
    },
    enabled: Boolean(customerId),
  })
}
